/**
 * Configuração do Grafana Faro lida do build (guia de observabilidade da Funifier).
 *
 * Sem `REACT_APP_FARO_API_KEY` não há configuração: o Faro não sobe e o app roda exatamente como
 * antes da integração. A chave vem do time de infraestrutura e vai na variável de ambiente do build;
 * ela viaja no bundle público, então não é segredo, mas também não fica no repositório.
 */
import { VERSION } from 'utils/constants';

/** Fixo e igual em todo ambiente: cada valor novo vira uma entrada nova na lista de apps do Grafana. */
export const FARO_APP_NAME = 'jogajunto-web';

export const FARO_COLLECTOR_URL = 'https://rum.funifier.com/collect';

const FARO_ENVIRONMENTS = ['production', 'staging', 'development'] as const;

export type FaroEnvironment = (typeof FARO_ENVIRONMENTS)[number];

export interface FaroSettings {
  apiKey: string;
  environment: FaroEnvironment;
  version: string;
}

type BuildEnv = Record<string, string | undefined>;

/**
 * Chave a chave, de propósito: o CRA troca cada `process.env.REACT_APP_X` pelo valor no build, mas
 * `process.env` inteiro vira um objeto com todas as variáveis `REACT_APP_*` embutido no bundle.
 */
const buildEnv = (): BuildEnv => ({
  REACT_APP_FARO_API_KEY: process.env.REACT_APP_FARO_API_KEY,
  REACT_APP_FARO_ENVIRONMENT: process.env.REACT_APP_FARO_ENVIRONMENT,
});

export function readFaroSettings(env: BuildEnv = buildEnv()): FaroSettings | null {
  const apiKey = env.REACT_APP_FARO_API_KEY?.trim();
  if (!apiKey) return null;

  return { apiKey, environment: readEnvironment(env.REACT_APP_FARO_ENVIRONMENT), version: VERSION };
}

/** Valor ausente ou desconhecido vira `development`: na dúvida, nunca contar como produção. */
function readEnvironment(value: string | undefined): FaroEnvironment {
  const environment = value?.trim().toLowerCase();
  return FARO_ENVIRONMENTS.find(known => known === environment) ?? 'development';
}
